import { ElementRef } from '@angular/core';
import { CellData } from '@ddt-web/data-service';
import { ColumnGroup, GridApi } from 'ag-grid-community';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

export class TableDataUtil {


	public static groupStatus = {
		ALL: 'all',
		NONE: 'none',
		SOME: 'some'
	};

	public static getDisplayedColumns(columnsObject: { [key: string]: { [key: string]: string | Object } }): string[] {
		const resultColumns = [];
		Object.keys(columnsObject).forEach(group => {
			Object.keys(columnsObject[group].columns).forEach(column => {
				if (columnsObject[group].columns[column]) {
					resultColumns.push(column);
				}
			});
		});
		return resultColumns;
	}

	public static getColumnsGroupObject(data: Array<{ [key: string]: CellData }>): { [key: string]: { [key: string]: string | Object } } {
		const result = {};
		Object.keys(data[0]).forEach(column => {
			const columnGroup = data[0][column].config.label.attributes.columnGroup;
			const columnSelected = data[0][column].config.label.attributes.columnSelected;
			if (columnGroup !== undefined) {
				if (result[columnGroup] === undefined) {
					result[columnGroup] = {};
					result[columnGroup].columns = {};
				}
				result[columnGroup].columns[column] = columnSelected;
			}
		});
		Object.keys(result).forEach(group => (result[group].status = this.getGroupStatus(group, result)));
		return result;
	}

	public static getGroupStatus(group: string, columnsObject: any): string {
		const allColumnsSelected = Object.keys(columnsObject[group].columns).every(column => columnsObject[group].columns[column] === true);
		const noColumnsSelected = Object.keys(columnsObject[group].columns).every(column => columnsObject[group].columns[column] === false);
		if (allColumnsSelected) {
			return this.groupStatus.ALL;
		} else if (noColumnsSelected) {
			return this.groupStatus.NONE;
		}
		return this.groupStatus.SOME;
	}

	/**
	 * @description clearColumnSeparators: removes the column separators attribute from all cell data items.
	 * @param {string[]} displayedColumns the columns to be displayed in the table.
	 * @param {Array<{ [key: string]: CellData }>} data: the data used to populate the table.
	 * @returns {Array<{ [key: string]: CellData }>} the data used to populate the table.
	 */
	public static clearColumnSeparators(displayedColumns: string[], data: Array<{ [key: string]: CellData }>): Array<{ [key: string]: CellData }> {
		displayedColumns.forEach(key => {
			data.forEach(dataItem => {
				const cell = dataItem[key];
				cell.config.label.attributes['borderClassName'] = '';
			});
		});
		return data;
	}

	/**
	 * @description addColumnSeparators: adds the columnBorder class attribute to the cell data used to render separator lines within the table.
	 * @param {string[]} hiddenColumns the columns that are hidden.
	 * @param {Array<{ [key: string]: CellData }>} sortedData: the sorted data used to populate the table.
	 * @returns {Array<{ [key: string]: CellData }>} the sorted data used to populate the table.
	 */
	public static addColumnSeparators(hiddenColumns: number[], sortedData: Array<{ [key: string]: CellData }>, columnsToDisplay?): Array<{ [key: string]: CellData }> {
		if (sortedData && sortedData.length) {
			if (isNil(columnsToDisplay)) {
				columnsToDisplay = [];
				Object.keys(sortedData[0]).forEach((key, index) => {
					const isHidden: boolean = hiddenColumns.includes(index);
					if (!isHidden) {
						columnsToDisplay.push(key);
					}
				});
			}
			columnsToDisplay.forEach((key, index) => {
				sortedData.forEach(dataItem => {
					const cell = dataItem[key];
					const currentGroupName = get(cell, 'config.label.attributes.columnGroup');
					if (currentGroupName) {
						const nextGroupName = get(dataItem[columnsToDisplay[index + 1]], 'config.label.attributes.columnGroup');
						if (currentGroupName !== nextGroupName) {
							cell.config.label.attributes['borderClassName'] = 'columnBorder';
						} else {
							cell.config.label.attributes['borderClassName'] = '';
						}
					}
				});
			});
		}
		return sortedData;
	}

	/**
	 * Checks the height of all visible cells and extends the header height to fit all columns in the table
	 * @param {ElementRef} elementRef
	 * @returns {number}
	 */
	public static getHeaderHeight(elementRef: ElementRef): number {
		const currentHeaderHeight: number = Math.max(
			...(Array.from(elementRef.nativeElement.querySelectorAll('.ag-header-row-column')) ?? []).map((header: any) => header.scrollHeight)
		);
		const headerCellHeights: number[] = (Array.from(elementRef.nativeElement.querySelectorAll('.header-row')) ?? [])
			.map((header: any) => header.scrollHeight + 32); // cell content height + padding
		return Math.max(...[currentHeaderHeight, ...headerCellHeights]);
	}

	public static updateHeaderHeight(rowData: any[], gridApi: GridApi, elementRef: ElementRef, headerHeight: number = 35): void {
		if (rowData?.length > 0) {
			gridApi.setHeaderHeight(TableDataUtil.getHeaderHeight(elementRef)); // Set header height based on the space needed
			gridApi.setGroupHeaderHeight(headerHeight); // Group header height is static
		} else {
			// Set height to 0 to hide both grid headers if there's no data
			gridApi.setHeaderHeight(0);
			gridApi.setGroupHeaderHeight(0);
		}
	}

	public static adjustTableHeight(newTableData: any[], gridApi: GridApi): void {
		if (gridApi) {
			if (newTableData.length === 0) {
				document.querySelector('.grid-container').setAttribute('style', 'height: 300px;');
				gridApi.setDomLayout('normal');
			} else if (newTableData.length < 25) {
				// Set table height to auto
				// Auto height will get the grid to fill the height of the contents, so the grid div should have no height set, the height is dynamic.
				document.querySelector('.grid-container').setAttribute('style', '');
				gridApi.setDomLayout('autoHeight');
			} else {
				// Set table height to fixed if there are more rows in the table
				document.querySelector('.grid-container').setAttribute('style', 'height: 70vh;');
				gridApi.setDomLayout('normal');
			}
		}
	}

	/**
	 * Moving the group header label in table by computing the transformX value of header group container
	 * and apply to group header label to move it in same proportion,
	 * so that it will be visible in the header as we scroll through the body of table
	 */
	public static setGroupHeaderStyle(isExpanded: boolean, headerId: string = 'performance', colGroup?: ColumnGroup): void {
		const headerGrpContainer = document.getElementsByClassName('ag-header-container')?.[0];
		const groupHeader = document.getElementById(colGroup?.getGroupId() ?? headerId);

		if (groupHeader) {
			if (isExpanded) {
				let translateXValue = +window.getComputedStyle(headerGrpContainer)?.getPropertyValue('transform')?.split(', ')?.[4];
				if (colGroup?.getLeft()) {
					translateXValue = -translateXValue > colGroup.getLeft() ? translateXValue + colGroup.getLeft() : 0;
				}
				groupHeader.style.transform = 'translateX(' + (-translateXValue) + 'px)';
				groupHeader.style.transitionDelay = '0.175s';
			} else {
				groupHeader.style.transform = null;
			}
		}
	}

	public static tableHeaderStickyOnDocumentScroll(): void {
		const headerElementRef = document.querySelector('[ref="gridHeader"]') as unknown as HTMLElement;
		const bodyElementRef = document.querySelector('[ref="eBodyViewport"]') as unknown as HTMLElement;
		if (!headerElementRef) { return; }
		const appHeader = document.querySelector('.ac-header') as unknown as HTMLElement;
		const tabHeader = document.querySelector('.sticky-container') as unknown as HTMLElement;
		const headerClientRect: DOMRect = headerElementRef.getBoundingClientRect();
		const bodyClientRect: DOMRect = bodyElementRef.getBoundingClientRect();
		const appHeaderHeight: number = appHeader?.getBoundingClientRect()?.height ?? 0;
		const tabHeaderHeight: number = tabHeader?.getBoundingClientRect()?.height ?? 0;
		const totalHeaderHeight: number = appHeaderHeight + tabHeaderHeight;
		const headerTopPosition: number = headerClientRect.top;
		const bodyTopPosition: number = bodyClientRect.top;
		const bodyBottomPosition: number = bodyClientRect.bottom;
		const headerHeight: number = headerClientRect.height;
		const isScrolledBackToTheTop: boolean = (bodyTopPosition > headerHeight && bodyTopPosition > totalHeaderHeight && !!bodyElementRef.style.paddingTop);
		const isScrolledDownPastHeaderTop: boolean = window.scrollY > headerTopPosition;

		if (
			!isScrolledBackToTheTop &&
			isScrolledDownPastHeaderTop &&
			bodyTopPosition - headerHeight <= totalHeaderHeight &&
			bodyBottomPosition > headerHeight + totalHeaderHeight
		) {
			bodyElementRef.style.paddingTop = `${headerHeight}px`;
			headerElementRef.style.position = 'fixed';
			headerElementRef.style.top = `${totalHeaderHeight}px`;
			headerElementRef.style.zIndex = '2';
			headerElementRef.style.width = `${bodyElementRef.offsetWidth}px`;
		} else {
			bodyElementRef.style.paddingTop = '';
			headerElementRef.style.position = 'relative';
			headerElementRef.style.top = '';
			headerElementRef.style.zIndex = '';
			headerElementRef.style.width = '';
		}
	}
}
